"use client";

import {
  AuthenticatedUser,
  WorkplaceRequestApprovalResponse,
  WorkplaceRequestElement,
  WorkplaceRequestLocationValue,
  WorkplaceRequestOccupiedResourceAction,
  WorkplaceRequestReleasedResourceAction,
  WorkplaceRequestTypeElement
} from "@gettactic/api";
import { Button } from "@gettactic/components/src/primitives/button";
import { useResourcesByIds } from "@gettactic/hooks";
import { Plus } from "lucide-react";
import React, { useMemo, useState } from "react";
import { ResourceFinderModal } from "@/features/map/components/modal/ResourceFinderModal";
import {
  ApprovalActionBox,
  ApprovalPendingNotice
} from "@/features/workplace-requests/components/ApprovalActionBox";
import { LinkedWorkflowsChecklist } from "@/features/workplace-requests/components/LinkedWorkflowsChecklist";
import { ReleasedDeskChoiceList } from "@/features/workplace-requests/components/ReleasedDeskChoiceList";
import { TakenDeskChoiceList } from "@/features/workplace-requests/components/TakenDeskChoiceList";
import {
  useDeskAutomationSteps,
  useVacatedDedicatedDesk
} from "@/features/workplace-requests/hooks/useDeskAutomation";
import type { FollowUp } from "@/features/workplace-requests/lib/followUpRequests";

interface RequestApprovalPanelProps {
  request: WorkplaceRequestElement;
  /** The request's own type — says whether a workspace is required. */
  selectedType: WorkplaceRequestTypeElement | undefined;
  /** Every request type, for naming the types the follow-ups are filed under. */
  workplaceRequestTypes: WorkplaceRequestTypeElement[];
  authenticatedUser: Pick<AuthenticatedUser, "user">;
  onApprovalResponse?: (status: WorkplaceRequestApprovalResponse) => void;
  isApprovalLoading?: boolean;
  /** Sets the request's location — used to add a workspace the type requires
   *  before approval when the request wasn't created with one. */
  onLocationChange?: (location: WorkplaceRequestLocationValue) => void;
  /** The requests approving this one would file, each unchecked to skip it. */
  followUps: FollowUp[];
  skippedWorkflowIds: string[];
  onToggleFollowUp: (workflowId: string) => void;
  /** Approver picks for the freed desks, by workflow id; empty means "as configured". */
  releasedDeskOverrides: Record<string, WorkplaceRequestReleasedResourceAction>;
  onReleasedDeskChange: (
    workflowId: string,
    action: WorkplaceRequestReleasedResourceAction
  ) => void;
  /** Approver picks for full desks, by workflow id; empty means "as configured". */
  occupiedDeskOverrides: Record<string, WorkplaceRequestOccupiedResourceAction>;
  onOccupiedDeskChange: (
    workflowId: string,
    action: WorkplaceRequestOccupiedResourceAction
  ) => void;
}

/**
 * The approval decision, directly under the request's title: whether to approve,
 * and what approving will create. It leads the request rather than sitting in
 * the sidebar among the request's properties — an approver opens the ticket to
 * make this call, so it is the first thing the page should offer.
 *
 * Approvers (admins / team managers, resolved by the backend via `can_approve`)
 * get the controls; the submitter instead sees a read-only notice in the same
 * slot, and everyone else nothing at all.
 */
export const RequestApprovalPanel: React.FC<RequestApprovalPanelProps> = ({
  request,
  selectedType,
  workplaceRequestTypes,
  authenticatedUser,
  onApprovalResponse,
  isApprovalLoading,
  onLocationChange,
  followUps,
  skippedWorkflowIds,
  onToggleFollowUp,
  releasedDeskOverrides,
  onReleasedDeskChange,
  occupiedDeskOverrides,
  onOccupiedDeskChange
}) => {
  const [isPickingLocation, setIsPickingLocation] = useState(false);

  // Only the rules that will actually run: a rule switched off frees nothing,
  // and one the approver is skipping is already settled by that decision.
  const { steps: enabledDeskSteps, runByWorkflowId } = useDeskAutomationSteps(
    request,
    selectedType
  );
  const deskSteps = useMemo(
    () =>
      enabledDeskSteps.filter(
        (step) => !skippedWorkflowIds.includes(step.workflowId)
      ),
    [enabledDeskSteps, skippedWorkflowIds]
  );
  // Which desk each move frees, looked up on the person, so the choice about
  // it can be named — or dropped when there is nothing to free.
  const vacatedDesk = useVacatedDedicatedDesk(deskSteps, runByWorkflowId);

  const deskResourceIds = useMemo(
    () =>
      deskSteps
        .map((step) => step.resourceId)
        .filter((id): id is string => id !== null),
    [deskSteps]
  );
  const { resources: deskResources } = useResourcesByIds(deskResourceIds);
  const deskName = (resourceId: string | null): string | null =>
    resourceId
      ? (deskResources.find((r) => r.id === resourceId)?.name ?? null)
      : null;

  const isAwaitingApproval = request.approval_status === "pending";
  const canApprove = Boolean(request.capabilities?.can_approve);
  const isSubmitter =
    !!request.submitter_user?.id &&
    request.submitter_user.id === authenticatedUser?.user?.id;

  // A type can defer "requires a workspace" to approval time — the request
  // may have been filed with none yet, but this is the one place it must be
  // added before an approver can act. A "floor" or "coordinates" location is
  // just a point on the map, not a real desk/room — only "resource" (or the
  // legacy resource_id alias, which only ever mirrors a resource location)
  // counts as an actual workspace.
  const hasWorkspace =
    request.location?.type === "resource" || !!request.resource_id;
  const needsLocationBeforeApproval =
    Boolean(selectedType?.requires_location) && !hasWorkspace;

  // A request filed as a dropped pin has no resource yet, but the pin still
  // tells the approver roughly where to look — thread it into the finder so
  // they land on the right floor and see it while picking (or placing) a desk.
  const pinnedLocation =
    request.location?.type === "coordinates" ? request.location : null;
  const referencePoint = pinnedLocation
    ? {
        x: pinnedLocation.x,
        y: pinnedLocation.y,
        floorId: pinnedLocation.floor_id
      }
    : undefined;
  // A "floor" location has no pin to render, but its floor_id is still the
  // one the requester/approver already chose — open the finder there too.
  const initialFloorId =
    request.location?.type === "floor" ? request.location.floor_id : undefined;

  if (!isAwaitingApproval) return null;

  if (!canApprove) {
    return isSubmitter ? (
      <div className="mt-5">
        <ApprovalPendingNotice />
      </div>
    ) : null;
  }

  return (
    <div className="mt-5 space-y-3">
      <ApprovalActionBox
        description="This request needs your approval before it can proceed."
        onApprove={() => onApprovalResponse?.("approve")}
        onReject={() => onApprovalResponse?.("reject")}
        isLoading={isApprovalLoading}
        approveDisabled={needsLocationBeforeApproval}
        approveDisabledReason="This request needs a workspace before it can be approved."
      >
        {followUps.length > 0 ? (
          <LinkedWorkflowsChecklist
            followUps={followUps}
            skippedWorkflowIds={skippedWorkflowIds}
            typeName={(typeId) =>
              workplaceRequestTypes.find((t) => t.id === typeId)?.name ?? null
            }
            onToggle={onToggleFollowUp}
            isDisabled={isApprovalLoading}
          />
        ) : null}

        <ReleasedDeskChoiceList
          steps={deskSteps}
          deskName={deskName}
          vacatedDesk={vacatedDesk}
          overrides={releasedDeskOverrides}
          onChange={onReleasedDeskChange}
          isDisabled={isApprovalLoading}
        />
        <TakenDeskChoiceList
          steps={deskSteps}
          deskResources={deskResources}
          overrides={occupiedDeskOverrides}
          onChange={onOccupiedDeskChange}
          isDisabled={isApprovalLoading}
        />
      </ApprovalActionBox>

      {needsLocationBeforeApproval ? (
        <Button
          variant="outline"
          className="gap-1.5"
          iconLeft={<Plus className="h-3.5 w-3.5" />}
          onClick={() => setIsPickingLocation(true)}
        >
          Add workspace
        </Button>
      ) : null}

      {/* Resource-only: a "floor" or "coordinates" location wouldn't satisfy
          `needsLocationBeforeApproval` above, so the Floor/Point modes have no
          place in a flow whose whole point is adding a real workspace. */}
      <ResourceFinderModal
        isOpen={isPickingLocation}
        onClose={() => setIsPickingLocation(false)}
        initialOfficeId={request.office_id}
        lockOffice
        referencePoint={referencePoint}
        initialFloorId={initialFloorId}
        onSelect={(resource) => {
          onLocationChange?.({ type: "resource", resource_id: resource.id });
          setIsPickingLocation(false);
        }}
      />
    </div>
  );
};
